@if (showOtherHotels || showDayShift || showLaterStay) {
  <div class="alt-availability" [attr.data-testid]="'alt-availability-root'">

    <!-- Other hotels -->
    @if (showOtherHotels) {
      <div class="alt-box" [attr.data-testid]="'alt-other-hotels'">
        <h3 class="alt-box-title">{{ 'av.alternativeAvailability.otherHotelsTitle' | translate }}</h3>

        @if (otherHotelsLoading) {
          <p-skeleton height="3rem" styleClass="mb-2" />
          <p-skeleton height="3rem" styleClass="mb-2" />
        } @else {
          @for (hotel of otherHotels; track hotel.hotelCode ?? hotel.displayName) {
            <div class="alt-row" [attr.data-testid]="'alt-other-hotel-' + (hotel.hotelCode ?? hotel.displayName)">
              <div class="alt-row-info">
                <span class="alt-row-name">{{ hotel.displayName }}</span>
                @if (hotel.hasAvailability) {
                  <span class="alt-row-rooms">{{ 'av.alternativeAvailability.roomsLeft' | translate:{ value: hotel.roomsAvailable } }}</span>
                } @else {
                  <span class="alt-row-none">{{ 'av.alternativeAvailability.noneHere' | translate }}</span>
                }
              </div>
              <div class="alt-row-action">
                @if (hotel.hasAvailability && hotel.minPrice != null) {
                  <span class="alt-row-price">
                    <span class="alt-from">{{ 'av.alternativeAvailability.startingFrom' | translate }}</span>
                    <span [innerHTML]="hotel.minPrice | displayPrice"></span>
                  </span>
                  @if (isLinkable(hotel)) {
                    <p-button size="small" (click)="selectOtherHotel(hotel)">{{ 'av.Select' | translate }}</p-button>
                  }
                }
              </div>
            </div>
          }
        }
      </div>
    }

    <!-- Day shift -->
    @if (showDayShift) {
      <div class="alt-box" [attr.data-testid]="'alt-day-shift'">
        <h3 class="alt-box-title">{{ 'av.alternativeAvailability.dayShiftTitle' | translate }}</h3>

        @if (dayShiftLoading) {
          <p-skeleton height="3rem" styleClass="mb-2" />
        } @else if (dayCells.length) {
          <div class="alt-day-cells" [attr.data-testid]="'alt-day-cells'">
            @for (cell of dayCells; track cell.offset) {
              <button
                type="button"
                class="alt-day-cell"
                [class.alt-day-cell-searched]="cell.offset === 0"
                [class.alt-day-cell-available]="cell.hasAvailability"
                [disabled]="!cell.hasAvailability || dayCellsLoading"
                [attr.data-testid]="'alt-day-cell-' + cell.offset"
                (click)="selectDay(cell)">
                @if (dayCellsLoading) {
                  <p-skeleton height="3.5rem" width="100%" />
                } @else {
                  <span class="alt-day-cell-dates">
                    <span class="alt-day-cell-date">
                      <span class="alt-day-cell-weekday">{{ cell.arrival | date:'EEE' }}</span>
                      <span>{{ cell.arrival | date:'d.M.' }}</span>
                    </span>
                    <span class="alt-day-cell-dash">–</span>
                    <span class="alt-day-cell-date">
                      <span class="alt-day-cell-weekday">{{ cell.departure | date:'EEE' }}</span>
                      <span>{{ cell.departure | date:'d.M.' }}</span>
                    </span>
                  </span>
                  @if (cell.hasAvailability) {
                    <span class="alt-day-cell-status">{{ 'av.alternativeAvailability.available' | translate }}</span>
                    @if (cell.minPrice != null) {
                      <span class="alt-day-cell-price">
                        {{ 'av.alternativeAvailability.startingFrom' | translate }}
                        <span [innerHTML]="cell.minPrice | displayPrice"></span>
                      </span>
                    }
                  } @else {
                    <span class="alt-row-none">{{ 'av.alternativeAvailability.noneHere' | translate }}</span>
                  }
                }
              </button>
            }
          </div>
          <div class="alt-day-nav">
            <p-button [outlined]="true" size="small" [disabled]="!canPageDays(-1)" (click)="pageDays(-1)">
              <span class="material-symbols-outlined">chevron_left</span>
              {{ 'av.alternativeAvailability.previousDays' | translate }}
            </p-button>
            <p-button [outlined]="true" size="small" [disabled]="!canPageDays(1)" (click)="pageDays(1)">
              {{ 'av.alternativeAvailability.nextDays' | translate }}
              <span class="material-symbols-outlined">chevron_right</span>
            </p-button>
          </div>
        }
      </div>
    }

    <!-- Next week -->
    @if (showLaterStay) {
      <div class="alt-box" [attr.data-testid]="'alt-later-stay'">
        <h3 class="alt-box-title">{{ (laterStayOption?.titleKey ?? '') | translate }}</h3>

        @if (laterStayLoading) {
          <p-skeleton height="3rem" styleClass="mb-2" />
        } @else if (laterStay) {
          <div class="alt-row alt-row-stacked" [attr.data-testid]="'alt-later-stay-row'">
            <div class="alt-row-head">
              <p-button [text]="true" [rounded]="true" size="small" [disabled]="!canStepLaterStay(-1)" [attr.aria-label]="'av.alternativeAvailability.previousDay' | translate" (click)="stepLaterStay(-1)">
                <span class="material-symbols-outlined">chevron_left</span>
              </p-button>
              <div class="alt-row-title">
                <span class="alt-row-name">{{ laterStay.arrival | date:'EEE d.M.y' }} – {{ laterStay.departure | date:'EEE d.M.y' }}</span>
              </div>
              <div class="alt-row-arrows">
                <p-button [text]="true" [rounded]="true" size="small" [disabled]="!canStepLaterStay(1)" [attr.aria-label]="'av.alternativeAvailability.nextDay' | translate" (click)="stepLaterStay(1)">
                  <span class="material-symbols-outlined">chevron_right</span>
                </p-button>
              </div>
            </div>
            <div class="alt-row-availability">
              @if (laterStayStepLoading) {
                <p-skeleton height="2rem" width="100%" />
              } @else {
                @if (laterStay.hasAvailability) {
                  <span class="alt-row-rooms">{{ 'av.alternativeAvailability.roomsLeft' | translate:{ value: laterStay.roomsAvailable } }}</span>
                } @else {
                  <span class="alt-row-none">{{ 'av.alternativeAvailability.noneHere' | translate }}</span>
                }
                @if (laterStay.hasAvailability && laterStay.minPrice != null) {
                  <div class="alt-row-action">
                    <span class="alt-row-price">
                      <span class="alt-from">{{ 'av.alternativeAvailability.startingFrom' | translate }}</span>
                      <span [innerHTML]="laterStay.minPrice | displayPrice"></span>
                    </span>
                    <p-button size="small" (click)="selectLaterStay()">{{ 'av.Select' | translate }}</p-button>
                  </div>
                }
              }
            </div>
          </div>
        }
      </div>
    }

  </div>
}
